import { cn } from '~/lib/utils'

type Option<T extends string | number> = {
  value: T
  label: React.ReactNode
  /** Langue du libellé quand elle diffère de la page (« English » dans une page française, RGAA 8.7). */
  lang?: string
}

type IProps<T extends string | number> = {
  /** Nom du groupe pour les lecteurs d'écran : ce qu'on y choisit. */
  label: string
  options: readonly Option<T>[]
  value: T
  onChange: (value: T) => void
  className?: string
}

/**
 * Choix d'une option parmi quelques-unes, en pastilles côte à côte : période
 * d'une courbe, thème, langue. Un groupe de boutons à `aria-pressed` plutôt
 * qu'un `<select>` : les options se voient toutes, et le choix courant se lit
 * sans ouvrir de liste.
 */
export function SegmentedControl<T extends string | number>({ label, options, value, onChange, className }: IProps<T>) {
  return (
    <div className={cn('flex flex-wrap gap-1', className)} role="group" aria-label={label}>
      {options.map(option => (
        <button
          key={option.value}
          type="button"
          lang={option.lang}
          onClick={() => onChange(option.value)}
          aria-pressed={value === option.value}
          className={cn(
            'rounded-full px-3 py-1 text-xs font-medium transition-colors',
            value === option.value
              ? 'bg-foreground text-background'
              : 'text-muted-foreground hover:bg-muted',
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}
